import React, { useEffect, useRef, useState } from 'react';
import { XIcon } from 'lucide-react';
interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const FOCUSABLE = 'button:not([disabled]), [href], iframe, input, select, textarea, [tabindex]:not([tabindex="-1"])';
export const WaitlistModal: React.FC<WaitlistModalProps> = ({
  isOpen,
  onClose
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isFormLoaded, setIsFormLoaded] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  // Mount, then fade in on the next frames; fade out before unmounting.
  useEffect(() => {
    if (isOpen) {
      returnFocusRef.current = document.activeElement as HTMLElement | null;
      setShouldRender(true);
      document.body.style.overflow = 'hidden';
      let secondFrame = 0;
      const firstFrame = requestAnimationFrame(() => {
        secondFrame = requestAnimationFrame(() => setIsAnimating(true));
      });
      return () => {
        cancelAnimationFrame(firstFrame);
        cancelAnimationFrame(secondFrame);
      };
    }
    setIsAnimating(false);
    document.body.style.overflow = '';
    if (returnFocusRef.current) {
      returnFocusRef.current.focus();
      returnFocusRef.current = null;
    }
    const timer = setTimeout(() => {
      setShouldRender(false);
      setIsFormLoaded(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [isOpen]);
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  useEffect(() => {
    if (isOpen && shouldRender) closeButtonRef.current?.focus();
  }, [isOpen, shouldRender]);
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);
  if (!shouldRender) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-200" data-mp="3qiejqiwnobvf">
      <div aria-hidden="true" onClick={onClose} className={`absolute inset-0 bg-ink-text/40 backdrop-blur-sm transition-opacity duration-300 ease-out ${isAnimating ? 'opacity-100' : 'opacity-0'}`} data-id="element-201" data-mp="3mjxa9spripau" />

      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="waitlist-title" className={`relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl bg-white shadow-card-hover transition-[opacity,transform] duration-300 ease-out ${isAnimating ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} data-id="element-202" data-mp="1i165yrzlog1s">
        <h2 id="waitlist-title" className="sr-only" data-id="element-203" data-mp="1d8mwa641a529">
          Join the Re:Joy waitlist
        </h2>
        <button ref={closeButtonRef} type="button" onClick={onClose} className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-cloud transition-colors duration-200 hover:bg-sandstone" aria-label="Close waitlist form" data-id="element-204" data-mp="ybvu5p2z6f8y">
          <XIcon className="text-deep-juniper" size={20} aria-hidden="true" data-id="element-205" data-mp="udg93qqgfgy6" />
        </button>

        <div className="relative max-h-[90vh] overflow-y-auto" data-id="element-206" data-mp="2jop58ltmxjt7">
          {!isFormLoaded && <div role="status" className="absolute inset-x-0 top-0 flex h-64 items-center justify-center text-plum-gray" data-id="element-207" data-mp="2onjv27q0s8mt">
              Loading the form…
            </div>}
          <iframe src="https://docs.google.com/forms/d/e/1FAIpQLSc9_VdBiAMhfaNIf6IBXW8KsGsLBQBFIe1TctaA_avWiBMtGQ/viewform?embedded=true" width="100%" height="2327" frameBorder="0" marginHeight={0} marginWidth={0} title="Re:Joy waitlist form" onLoad={() => setIsFormLoaded(true)} data-id="element-208" data-mp="eoxiewpkahwz">
            Loading…
          </iframe>
        </div>
      </div>
    </div>;
};